import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { Space } from "antd-mobile";
import { Button } from "../Base/Button";
import { Tabs, Tab } from "../Base/Tabs";
import { Form, FormItem } from "../Base/Form";
import { Checkbox } from "../Base/Checkbox";
import { AntdLink } from "../Base/AntdTypography";
import { Toast } from "../Base/Toast";
import Turnstile from "../Turnstile";
import { triggerAgreementConfirmation } from "../AgreementConfirmation";
import { ProtocolDialog } from "../ProtocolDialog";
import { EnhancedInput } from "../Base/Input/EnhancedInput";
import { ImageCode } from "../Base/ImageCode";
import CountryCodeSelect from "../Base/CountrySelect/CountryCodeSelect";
import { LazyImage } from "../LazyImage/LazyImage";
import { useUserAuth, useUserStore } from "../../modules";
import { useStartupDataFlattened } from "../../modules/startup";
import { LoginRequest } from "../../modules/user/types/auth.types";
import { useCountdown } from "../../hooks/useUtils";
import { sendSmsCode } from "../../api/sms";
import { getAuthConfig } from "../../api/auth";
import { useAuthConfigStore } from "../../stores";
import utils from "../../utils/utils";
import { wsUtils } from "../../utils/wsUtils";
import {
  isWebPushSupported,
  subscribeHasLogin,
  getPushSubscriptionStatus,
} from "../../utils/webPush";
import {
  processPhone,
  getPhonePattern,
  validatePhone,
} from "../../utils/phoneValidation";
import { useT } from "../../i18n/hooks";
import {
  useLoginRegisterConfig,
  useResetPasswordIpInCooldown,
  useResetPasswordEmailIpInCooldown,
  useOnlineServiceSupportUrl,
} from "../../modules/homeDisplay";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { ResetPasswordDialog } from "../../components/ResetPasswordDialog";
import { ResetPasswordByEmailDialog } from "../../components/ResetPasswordByEmailDialog";
import { PasswordRecoveryDialog } from "../../components/PasswordRecoveryDialog";
import { TooManyPasswordAttemptsDialog } from "../../components/TooManyPasswordAttemptsDialog";
import trackingService from "@/services/trackingService";
import { PhoneIcon, UserIcon } from "../../components/Icon/IconSvgMap";
import { AdUtils } from "../../utils";
import { useNavigation } from "../../utils/navigation";
import { usePopupQueue } from "../../contexts";
import {
  LOGIN_REGISTER_POPUP_EVENT,
  CLOSE_LOGIN_REGISTER_POPUP_EVENT,
  consumePendingLoginRegisterPopup,
  releaseLoginRegisterPopupListener,
} from "./triggerLoginRegisterPopup";
import type {
  LoginRegisterPopupProps,
  LoginRegisterFormValues,
  LoginRegisterTab,
  OpenLoginRegisterPopupOptions,
} from "./LoginRegisterPopup.types";
import { FooterCooperationPlatformsTopRow } from "../Footer/FooterCooperationPlatforms";
import "../Footer/FooterCooperationPlatforms.scss";
import { PromoBubble } from "../PromoBubble";
import "./LoginRegisterPopup.scss";

export function LoginRegisterPopup({ className = "" }: LoginRegisterPopupProps) {
  const t = useT();
  const navigate = useNavigation();
  const { login, loginLoading } = useUserAuth();
  const { enableWebPush } = useStartupDataFlattened();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  const loginRegisterConfig = useLoginRegisterConfig();
  const resetPasswordIpInCooldown = useResetPasswordIpInCooldown();
  const resetPasswordEmailIpInCooldown = useResetPasswordEmailIpInCooldown();
  const onlineServiceSupportUrl = useOnlineServiceSupportUrl();

  const homeRegisterPopupEnabled = useHomeDisplayStore(
    s => s.data?.platformLogo?.homeRegisterPopupEnabled
  );
  const homeRegisterPopupBanner = useHomeDisplayStore(
    s => s.data?.platformLogo?.homeRegisterPopupBanner
  );
  const hasBanner = !!(homeRegisterPopupEnabled && homeRegisterPopupBanner);

  const [visible, setVisible] = useState(false);
  const [fromPath, setFromPath] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [loginMethod, setLoginMethod] = useState<LoginRegisterTab>("sms");
  const [showPassword, setShowPassword] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [wafToken, setWafToken] = useState<string>("");
  const [imageCode, setImageCode] = useState<string>("");
  const [selectedCountryId, setSelectedCountryId] = useState<string>(
    utils.getObject("countryId")
  );
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("");
  const [protocolDialogVisible, setProtocolDialogVisible] = useState(false);
  const [currentProtocolPath, setCurrentProtocolPath] = useState("");
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [inviteCode, setInviteCode] = useState<string>("");
  const [resetPasswordByEmailVisible, setResetPasswordByEmailVisible] =
    useState(false);
  const [passwordRecoveryVisible, setPasswordRecoveryVisible] = useState(false);
  const [tooManyAttemptsVisible, setTooManyAttemptsVisible] = useState(false);

  const { pause, resume } = usePopupQueue();
  const wasAutoTriggeredRef = useRef(false);

  const { timeLeft, isActive, start, reset } = useCountdown(60);
  const { enableWafCode, wafSiteKey, formKey, disablePhone, setAuthConfig } =
    useAuthConfigStore();

  const enableLoginMobileVerifyCode =
    loginRegisterConfig?.enableLoginMobileVerifyCode ?? false;
  const enableLoginImageCode =
    loginRegisterConfig?.enableLoginImageCode ?? false;
  const enableProtocolText =
    loginRegisterConfig?.enableDefaultCheckProtocol ?? false;
  const defaultProtocolText =
    loginRegisterConfig?.defaultProtocolText ??
    "I confirm that I am 21 years old and I have read the [Terms of use](/protocol/terms) and [Privacy policy](/protocol/privacy)";
  const registerPromoText =
    loginRegisterConfig?.registerFirstChargePromoText?.trim() ?? "";

  const parseMarkdownLinks = (text: string): string =>
    text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="javascript:void(0)" data-protocol-path="$2">$1</a>'
    );
  const protocolHtml = parseMarkdownLinks(defaultProtocolText);

  const handleProtocolLinkClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A" && target.hasAttribute("data-protocol-path")) {
      e.preventDefault();
      const path = target.getAttribute("data-protocol-path");
      if (path) {
        setCurrentProtocolPath(path);
        setProtocolDialogVisible(true);
      }
    }
  };

  const enableCodeLogin = useMemo(() => {
    if (enableLoginMobileVerifyCode) return true;
    if (disablePhone) return false;
    return false;
  }, [enableLoginMobileVerifyCode, disablePhone]);

  // When SMS is disabled by config, fall back to password tab
  useEffect(() => {
    if (!enableCodeLogin && loginMethod === "sms") {
      setLoginMethod("password");
    }
  }, [enableCodeLogin, loginMethod]);

  useEffect(() => {
    if (!visible) return;
    const loadAuthConfig = async () => {
      try {
        const response = await getAuthConfig();
        if (response) {
          setAuthConfig({
            enableWafCode: response.enableWafCode || false,
            wafSiteKey: response.wafSiteKey || "",
            formKey: response.formKey || "",
            disablePhone: response.disablePhone || false,
          });
        }
      } catch {
        // ignore
      }
    };
    loadAuthConfig();
  }, [visible, setAuthConfig]);

  useEffect(() => {
    const countryId = utils.getObject("countryId");
    const country = window._config?.countryList?.find(c => c.id === countryId);
    if (country && !selectedPhoneCode) {
      setSelectedPhoneCode(country.phoneCode);
      setSelectedCountryId(country.id);
    }
  }, [selectedPhoneCode]);

  const phonePattern = useMemo(
    () => getPhonePattern(selectedCountryId),
    [selectedCountryId]
  );

  useEffect(() => {
    if (visible) {
      const searchParams = new URLSearchParams(location.search);
      const pParam = searchParams.get("p") || utils.getObject("invitedCode");
      if (pParam) setInviteCode(pParam);
    }
  }, [visible]);

  useEffect(() => {
    const applyOpen = (detail?: OpenLoginRegisterPopupOptions) => {
      // "register" mode maps to SMS tab (backend auto-registers new phone numbers)
      const tab: LoginRegisterTab =
        detail?.defaultMode === "register"
          ? "sms"
          : (detail?.defaultTab ?? "sms");
      setLoginMethod(tab);
      if (detail?.from) setFromPath(detail.from);
      setVisible(true);
      if (detail?.isAutoTrigger) {
        pause();
        wasAutoTriggeredRef.current = true;
      }
    };
    const handleOpen = (event: Event) => {
      applyOpen((event as CustomEvent<OpenLoginRegisterPopupOptions>).detail);
    };
    const handleClose = () => {
      setVisible(false);
      if (wasAutoTriggeredRef.current) {
        resume();
        wasAutoTriggeredRef.current = false;
      }
    };

    window.addEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    // 消费监听器挂载前缓存的 open 请求（如首页自动触发早于本组件 commit）
    const pending = consumePendingLoginRegisterPopup();
    if (pending) applyOpen(pending);
    return () => {
      releaseLoginRegisterPopupListener();
      window.removeEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    };
  }, [pause, resume]);

  useEffect(() => {
    if (isAuthenticated && visible) {
      setVisible(false);
      if (wasAutoTriggeredRef.current) {
        resume();
        wasAutoTriggeredRef.current = false;
      }
    }
  }, [isAuthenticated, visible, resume]);

  const handleFieldsChange = useCallback(() => {
    setTimeout(() => {
      const values = form.getFieldsValue();
      const errors = form.getFieldsError();
      const hasErrors = errors.some(
        (field: { errors: string[] }) => field.errors.length > 0
      );
      const hasEmptyFields =
        loginMethod === "password"
          ? !values.mobile || !values.password
          : !values.mobile || !values.code;
      const protocolOk = values.agreeTerms === true;
      setIsFormValid(!hasErrors && !hasEmptyFields && protocolOk);
      const errorMap: Record<string, boolean> = {};
      errors.forEach((field) => {
        errorMap[String(field.name[0])] = field.errors.length > 0;
      });
      setFormErrors(errorMap);
    }, 0);
  }, [form, loginMethod]);

  const handleGetSmsCode = async () => {
    try {
      const mobile = form.getFieldValue("mobile")?.trim?.() ?? "";
      if (!mobile || !selectedPhoneCode) {
        form.setFields([{ name: "mobile", errors: [t("auth.pleaseEnterMobile")] }]);
        return;
      }
      const countryCodeDigits = (selectedPhoneCode || "").replace(/\D/g, "");
      const fullPhone = countryCodeDigits ? `${countryCodeDigits}${mobile}` : mobile;
      const validationResult = validatePhone(fullPhone, selectedCountryId);
      if (!validationResult.valid) {
        form.setFields([{ name: "mobile", errors: [t("auth.pleaseEnterValidMobile")] }]);
        return;
      }
      setSmsLoading(true);
      AdUtils.onVerifyCodeBegin();
      const response = await sendSmsCode(processPhone(mobile), "login", selectedCountryId);
      if (response.status === 200) {
        reset();
        start();
      } else {
        throw new Error(response.message || t("auth.sendSmsFailed"));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t("auth.sendSmsFailed");
      form.setFields([{ name: "mobile", errors: [errorMessage] }]);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleAuthSuccess = async (
    user: import("../../modules/user/types").User,
    extra?: { pixelEvent?: unknown },
    isRegister = false
  ) => {
    if (isRegister) {
      AdUtils.onRegister(extra?.pixelEvent);
      utils.setObject("invitedCode", null);
      utils.setObject("has_registered", null);
      utils.setObject("__auuid", null);
      utils.setObject("__uid", null);
      document.cookie = "__auuid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = "__uid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    } else {
      AdUtils.onLogin();
    }

    const userId = user.userId || utils.getObject("user")?.userId;
    trackingService.trackLoginSuccess(userId).catch(() => {});
    utils.setObject("has_registered", null);

    try {
      await wsUtils.open("global");
    } catch {
      // ignore
    }

    if (enableWebPush && isWebPushSupported()) {
      try {
        const { permission } = await getPushSubscriptionStatus();
        if (permission === "granted") subscribeHasLogin();
      } catch {
        // ignore
      }
    }

    setVisible(false);
    form.resetFields();
    setImageCode("");

    if (wasAutoTriggeredRef.current) {
      resume();
      wasAutoTriggeredRef.current = false;
    }

    if (fromPath) {
      navigate.navigate(fromPath, { replace: true });
      setFromPath(null);
    }
  };

  const doLogin = async (values: LoginRegisterFormValues) => {
    try {
      trackingService.trackLoginStart().catch(() => {});

      if (enableWafCode && !wafToken) {
        Toast.failed(t("auth.pleaseCompleteVerification"));
        return;
      }

      const processedMobile =
        loginMethod === "sms" && values.mobile ? processPhone(values.mobile) : values.mobile;
      const processedUsername =
        loginMethod === "password" && values.mobile
          ? values.mobile.replace(/^0+/, "")
          : values.mobile;
      const deviceId = utils.getDeviceId();

      let response;
      if (loginMethod === "password") {
        const loginData = {
          username: processedUsername!,
          password: values.password!,
          remember: true,
          loginType: "username" as const,
          wafCode: enableWafCode ? wafToken : undefined,
          imageCode: enableLoginImageCode ? imageCode : undefined,
        } as LoginRequest & { deviceId?: string; imageCode?: string };
        if (deviceId) loginData.deviceId = deviceId;
        response = await login(loginData);
      } else {
        // SMS: /api/auth/phone_login — backend auto-registers unknown numbers
        const loginData = {
          username: processedUsername!,
          phone: processedMobile!,
          code: values.code,
          remember: values.remember,
          loginType: "mobile" as const,
          countryId: selectedCountryId,
          inviteCode: inviteCode || undefined,
          wafCode: enableWafCode ? wafToken : undefined,
          imageCode: enableLoginImageCode ? imageCode : undefined,
        } as LoginRequest & { deviceId?: string; inviteCode?: string; imageCode?: string };
        if (deviceId) loginData.deviceId = deviceId;
        response = await login(loginData);
      }

      if (response?.user) {
        await handleAuthSuccess(response.user, response.extra, !!response.firebaseRegister);
      } else {
        trackingService.trackLoginFailed("Login failed").catch(() => {});
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t("auth.loginFailed");
      trackingService.trackLoginFailed(errorMsg).catch(() => {});
      Toast.failed(errorMsg);
    }
  };

  const handleSubmit = (values: LoginRegisterFormValues) => {
    triggerAgreementConfirmation(() => doLogin(values), undefined);
  };

  const handleClose = () => {
    setVisible(false);
    setFromPath(null);
    form.resetFields();
    setImageCode("");
    if (wasAutoTriggeredRef.current) {
      resume();
      wasAutoTriggeredRef.current = false;
    }
  };

  const handleTabChange = (key: string) => {
    setLoginMethod(key as LoginRegisterTab);
    form.resetFields();
    setImageCode("");
    setIsFormValid(false);
    setFormErrors({});
  };

  return (
    <>
      <AntdMobilePopup
        visible={visible}
        onClose={handleClose}
        position="bottom"
        className={`login-register-popup ${className}`}
        bodyClassName="login-register-popup__body"
        maskClassName="login-register-popup__mask"
        closeOnSwipe={false}
        destroyOnClose
      >
        {/* Banner / header area — close button always lives here */}
        <div
          className={`login-register-popup__banner${hasBanner ? " login-register-popup__banner--image" : ""}`}
        >
          {hasBanner && (
            <LazyImage
              src={utils.getImageUrl(homeRegisterPopupBanner!)}
              alt="register banner"
              width="100%"
              height="100%"
            />
          )}
          <button
            className="login-register-popup__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable form area */}
        <div className={`login-register-popup__scroll${hasBanner ? " login-register-popup__scroll--elevated" : ""}`}>
          {/* Tabs: SMS first (default), then Password */}
          <div className="login-register-popup__tabs">
            <Tabs activeKey={loginMethod} align="left" onChange={handleTabChange}>
              {enableCodeLogin && <Tab key="sms" title={t("auth.smsTab")} />}
              <Tab key="password" title={t("auth.passwordTab")} />
            </Tabs>
          </div>

          {/* Form */}
          <div className="login-register-popup__form">
            <Form
              form={form}
              onFinish={handleSubmit}
              onFieldsChange={handleFieldsChange}
              layout="vertical"
              className="login-register-form"
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {/* SMS tab */}
                {loginMethod === "sms" && (
                  <FormItem
                    name="mobile"
                    rules={[
                      { required: true, message: t("auth.pleaseEnterMobile") },
                      {
                        validator: (_, value) => {
                          if (!value || !selectedPhoneCode) return Promise.resolve();
                          const digits = (selectedPhoneCode || "").replace(/\D/g, "");
                          const full = digits ? `${digits}${value}` : value;
                          if (!phonePattern.test(full)) {
                            return Promise.reject(
                              new Error(t("auth.pleaseEnterValidMobile"))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <EnhancedInput
                      placeholder={t("auth.mobile")}
                      prefixIcon={{
                        icon: (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <CountryCodeSelect
                              value={selectedCountryId}
                              onChange={(countryId, phoneCode) => {
                                setSelectedCountryId(countryId);
                                setSelectedPhoneCode(phoneCode);
                                form.validateFields(["mobile"]);
                              }}
                            />
                            <span
                              style={{
                                color: "var(--color-border-control)",
                                margin: "0 0.5rem 0.375rem 0",
                              }}
                            >
                              |
                            </span>
                            <PhoneIcon />
                          </div>
                        ),
                        variant: "golden",
                        isComplexComponent: true,
                      }}
                      error={formErrors.mobile}
                    />
                  </FormItem>
                )}

                {loginMethod === "sms" && (
                  <FormItem
                    name="code"
                    rules={[
                      { required: true, message: t("auth.pleaseEnterSms") },
                      { len: 6, message: t("auth.smsCodeLength") },
                    ]}
                  >
                    <EnhancedInput
                      placeholder={t("auth.smsCodePlaceholder") || t("auth.sms")}
                      prefixIcon={{ icon: "message" }}
                      suffixContent={{
                        text: isActive
                          ? t("common.resendIn", { time: timeLeft })
                          : t("common.getCode"),
                        onClick: handleGetSmsCode,
                        loading: smsLoading,
                        disabled: isActive || smsLoading,
                        variant: "secondary",
                        size: "sm",
                        shape: "default",
                        className: "login-register-popup__send-code-btn",
                      }}
                      error={formErrors.code}
                    />
                  </FormItem>
                )}

                {/* Password tab */}
                {loginMethod === "password" && (
                  <FormItem
                    name="mobile"
                    rules={[
                      {
                        required: true,
                        message:
                          t("auth.pleaseEnterAccount") || "Please enter your account",
                      },
                    ]}
                  >
                    <EnhancedInput
                      placeholder={
                        t("auth.loginPlaceholder") || "Phone / Email / User ID"
                      }
                      prefixIcon={{ icon: <UserIcon />, variant: "golden" }}
                      error={formErrors.mobile}
                    />
                  </FormItem>
                )}

                {loginMethod === "password" && (
                  <>
                    <FormItem
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: t("auth.pleaseEnterPassword"),
                        },
                        { min: 8, message: t("auth.passwordTooShort") },
                      ]}
                    >
                      <EnhancedInput
                        placeholder={t("auth.password")}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        prefixIcon={{ icon: "password" }}
                        suffixContent={{
                          icon: showPassword ? "eye" : "eye-closed",
                          onClick: () => setShowPassword(!showPassword),
                        }}
                        error={formErrors.password}
                      />
                    </FormItem>

                    {(loginRegisterConfig?.enableMobileResetPassword ||
                      loginRegisterConfig?.enableEmailResetPassword) && (
                      <div className="login-register-popup__forgot-row">
                        <AntdLink
                          variant="primary"
                          underline
                          onClick={() => {
                            const phoneOn =
                              !!loginRegisterConfig?.enableMobileResetPassword;
                            const emailOn =
                              !!loginRegisterConfig?.enableEmailResetPassword;
                            if (phoneOn && emailOn) {
                              setPasswordRecoveryVisible(true);
                            } else if (phoneOn) {
                              if (resetPasswordIpInCooldown) {
                                setTooManyAttemptsVisible(true);
                              } else {
                                setResetPasswordVisible(true);
                              }
                            } else {
                              if (resetPasswordEmailIpInCooldown) {
                                setTooManyAttemptsVisible(true);
                              } else {
                                setResetPasswordByEmailVisible(true);
                              }
                            }
                          }}
                          className="login-register-popup__forgot-link"
                        >
                          {t("auth.forgotPassword") || "Forgot?"}
                        </AntdLink>
                      </div>
                    )}
                  </>
                )}

                {/* Image captcha */}
                {enableLoginImageCode && (
                  <FormItem name="imageCode">
                    <ImageCode
                      value={imageCode}
                      onChange={setImageCode}
                      error={!!formErrors.imageCode}
                    />
                  </FormItem>
                )}

                {/* Turnstile WAF */}
                {enableWafCode && wafSiteKey && (
                  <div className="login-register-popup__turnstile">
                    <Turnstile
                      key={formKey}
                      siteKey={wafSiteKey}
                      onChange={token => setWafToken(token)}
                    />
                  </div>
                )}

                {/* Submit */}
                <div className="login-register-popup__submit-btn-wrap">
                  {loginMethod === "sms" && <PromoBubble text={registerPromoText} />}
                  <Button
                    type="submit"
                    block
                    size="large"
                    variant="primary"
                    loading={loginLoading}
                    disabled={
                      !isFormValid || loginLoading || (enableWafCode && !wafToken)
                    }
                    className="login-register-popup__submit-btn"
                  >
                    {t(loginMethod === "sms" ? "auth.registerLogin" : "auth.login")}
                  </Button>
                </div>

                {/* Terms */}
                <div className="login-register-popup__terms">
                  <FormItem
                    name="agreeTerms"
                    valuePropName="checked"
                    initialValue={enableProtocolText}
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(t("auth.agreeTermsRequired"))
                              ),
                      },
                    ]}
                  >
                    <Checkbox variant="golden">
                      <span
                        className="login-register-popup__terms-text"
                        dangerouslySetInnerHTML={{ __html: protocolHtml }}
                        onClick={handleProtocolLinkClick}
                      />
                    </Checkbox>
                  </FormItem>
                </div>

                {/* Compliance */}
                <div className="login-register-popup__compliance">
                  <p className="login-register-popup__compliance-intro">
                    The following personalities are NOT ALLOWED to register
                    and/or play in this online gaming website:
                  </p>
                  <ul className="login-register-popup__compliance-list">
                    <li>
                      Government Official or employee connected directly with
                      the operation of the Government or any of its agencies.
                    </li>
                    <li>
                      Members of the Armed Forces of the Philippines, including
                      the Army, Navy, Air Force, or the Philippine National
                      Police.
                    </li>
                    <li>Persons under 21 years of age.</li>
                    <li>
                      Persons included in the PAGCOR's National Database of
                      Restricted Persons (NDRP).
                    </li>
                    <li>
                      Gaming Employment License (GEL) holder. Funds or credits
                      in the account of player who is found ineligible to play
                      shall mean forfeiture of said funds/credits in favor of
                      the Government.
                    </li>
                  </ul>
                  <p className="login-register-popup__compliance-footer">
                    Keep online gaming private. Avoid playing online games in
                    open and public spaces.
                  </p>
                </div>

                <div className="login-register-popup__coop-top" aria-hidden>
                  <FooterCooperationPlatformsTopRow />
                </div>

                {/* PAGCOR logos */}
                <div className="login-register-popup__pagcor">
                  <div className="login-register-popup__pagcor-logos">
                    <img
                      src="/assets/pagcor-logo.png"
                      alt="PAGCOR"
                      className="login-register-popup__pagcor-img"
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <img
                      src="/assets/21plus-logo.png"
                      alt="21+"
                      className="login-register-popup__pagcor-img"
                      onError={e => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                  <p className="login-register-popup__powered-by">
                    Powered by DigiPlus, a Fortune Southeast Asia 500 company
                  </p>
                </div>
              </Space>
            </Form>
          </div>
        </div>
      </AntdMobilePopup>

      {/* Protocol Dialog */}
      <ProtocolDialog
        visible={protocolDialogVisible}
        onClose={() => setProtocolDialogVisible(false)}
        protocolPath={currentProtocolPath}
      />

      {/* Password Recovery Dialogs */}
      {loginRegisterConfig?.enableMobileResetPassword &&
        loginRegisterConfig?.enableEmailResetPassword && (
          <PasswordRecoveryDialog
            visible={passwordRecoveryVisible}
            onClose={() => setPasswordRecoveryVisible(false)}
            onSelectMethod={method => {
              setPasswordRecoveryVisible(false);
              if (method === "phone") {
                if (resetPasswordIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordVisible(true);
                }
              } else {
                if (resetPasswordEmailIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordByEmailVisible(true);
                }
              }
            }}
          />
        )}

      {loginRegisterConfig?.enableMobileResetPassword && (
        <ResetPasswordDialog
          visible={resetPasswordVisible}
          onClose={() => setResetPasswordVisible(false)}
          onSuccess={() => {
            setResetPasswordVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          resetMethods={loginRegisterConfig?.resetPasswordMethods}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      {loginRegisterConfig?.enableEmailResetPassword && (
        <ResetPasswordByEmailDialog
          visible={resetPasswordByEmailVisible}
          onClose={() => setResetPasswordByEmailVisible(false)}
          onSuccess={() => {
            setResetPasswordByEmailVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      <TooManyPasswordAttemptsDialog
        visible={tooManyAttemptsVisible}
        onClose={() => {
          setTooManyAttemptsVisible(false);
          useHomeDisplayStore.getState().refreshConfig();
        }}
        supportUrl={onlineServiceSupportUrl}
      />
    </>
  );
}

export default LoginRegisterPopup;
